/**
 * User engagement event tracking
 */

import { analytics } from '../index';

export const engagementEvents = {
  // Feature usage
  featureUsed: async (data: {
    featureName: string;
    featureArea: string;
    isFirstTime: boolean;
    metadata?: Record<string, any>;
  }) => {
    await analytics.track('feature_used', {
      category: 'engagement',
      feature_name: data.featureName,
      feature_area: data.featureArea,
      is_first_time: data.isFirstTime,
      ...data.metadata,
    });
  },

  // Tutorial/onboarding events
  tutorialStarted: async (data: {
    tutorialName: string;
    totalSteps: number;
  }) => {
    await analytics.track('tutorial_started', {
      category: 'engagement',
      tutorial_name: data.tutorialName,
      total_steps: data.totalSteps,
    });
  },

  tutorialStepCompleted: async (data: {
    tutorialName: string;
    stepNumber: number;
    stepName: string;
    timeSpent: number; // seconds
  }) => {
    await analytics.track('tutorial_step_completed', {
      category: 'engagement',
      tutorial_name: data.tutorialName,
      step_number: data.stepNumber,
      step_name: data.stepName,
      time_spent: data.timeSpent,
    });
  },

  tutorialCompleted: async (data: {
    tutorialName: string;
    completionRate: number;
    totalTimeSpent: number; // seconds
    skippedSteps: number;
  }) => {
    await analytics.track('tutorial_completed', {
      category: 'engagement',
      tutorial_name: data.tutorialName,
      completion_rate: Math.round(data.completionRate * 100),
      total_time_spent: data.totalTimeSpent,
      skipped_steps: data.skippedSteps,
    });
  },

  // Help/support events
  helpOpened: async (data: {
    helpType: 'documentation' | 'video' | 'chat' | 'faq';
    context: string;
    searchQuery?: string;
  }) => {
    await analytics.track('help_opened', {
      category: 'engagement',
      help_type: data.helpType,
      context: data.context,
      search_query: data.searchQuery,
    });
  },

  feedbackProvided: async (data: {
    feedbackType: 'bug' | 'feature_request' | 'general' | 'nps';
    rating?: number;
    hasComment: boolean;
    context: string;
  }) => {
    await analytics.track('feedback_provided', {
      category: 'engagement',
      feedback_type: data.feedbackType,
      rating: data.rating,
      has_comment: data.hasComment,
      context: data.context,
    });
  },

  // Sharing/collaboration events
  contentShared: async (data: {
    contentType: 'budget' | 'report' | 'goal' | 'achievement';
    shareMethod: 'link' | 'email' | 'social';
    isPublic: boolean;
  }) => {
    await analytics.track('content_shared', {
      category: 'engagement',
      content_type: data.contentType,
      share_method: data.shareMethod,
      is_public: data.isPublic,
    });
  },

  collaboratorInvited: async (data: {
    inviteMethod: 'email' | 'link';
    permissionLevel: 'view' | 'edit' | 'admin';
    isFirstInvite: boolean;
  }) => {
    await analytics.track('collaborator_invited', {
      category: 'engagement',
      invite_method: data.inviteMethod,
      permission_level: data.permissionLevel,
      is_first_invite: data.isFirstInvite,
    });
  },

  // Settings/preferences events
  settingsChanged: async (data: {
    settingCategory: string;
    settingName: string;
    oldValue?: string;
    newValue: string;
  }) => {
    await analytics.track('settings_changed', {
      category: 'engagement',
      setting_category: data.settingCategory,
      setting_name: data.settingName,
      new_value: data.newValue,
    });
  },

  themeChanged: async (data: {
    fromTheme: string;
    toTheme: string;
    trigger: 'manual' | 'system' | 'schedule';
  }) => {
    await analytics.track('theme_changed', {
      category: 'engagement',
      from_theme: data.fromTheme,
      to_theme: data.toTheme,
      trigger: data.trigger,
    });
  },

  // Search events
  searchPerformed: async (data: {
    searchType: 'global' | 'transactions' | 'accounts' | 'help';
    resultCount: number;
    clickedResultPosition?: number;
    timeToClick?: number; // milliseconds
  }) => {
    await analytics.track('search_performed', {
      category: 'engagement',
      search_type: data.searchType,
      result_count: data.resultCount,
      clicked_result_position: data.clickedResultPosition,
      time_to_click: data.timeToClick,
    });
  },

  // Export/import events
  dataExported: async (data: {
    exportType: 'csv' | 'pdf' | 'json' | 'excel';
    dataCategory: 'transactions' | 'budget' | 'report' | 'all';
    recordCount: number;
    fileSize: number; // bytes
  }) => {
    await analytics.track('data_exported', {
      category: 'engagement',
      export_type: data.exportType,
      data_category: data.dataCategory,
      record_count: data.recordCount,
      file_size: data.fileSize,
    });
  },

  dataImported: async (data: {
    importType: 'csv' | 'bank_file' | 'json';
    dataCategory: 'transactions' | 'budget' | 'accounts';
    recordCount: number;
    successRate: number;
    duration: number; // seconds
  }) => {
    await analytics.track('data_imported', {
      category: 'engagement',
      import_type: data.importType,
      data_category: data.dataCategory,
      record_count: data.recordCount,
      success_rate: Math.round(data.successRate * 100),
      duration: data.duration,
    });
  },

  // Error tracking
  errorOccurred: async (data: {
    errorType: 'network' | 'validation' | 'permission' | 'unknown';
    errorCode?: string;
    context: string;
    isRecoverable: boolean;
    userAction?: string;
  }) => {
    await analytics.track('error_occurred', {
      category: 'engagement',
      error_type: data.errorType,
      error_code: data.errorCode,
      context: data.context,
      is_recoverable: data.isRecoverable,
      user_action: data.userAction,
    });
  },

  // Session events
  sessionStarted: async () => {
    await analytics.track('session_started', {
      category: 'engagement',
      timestamp: new Date().toISOString(),
    });
  },

  sessionEnded: async (data: {
    duration: number; // seconds
    pageViewCount: number;
    activeTime: number; // seconds
    idleTime: number; // seconds
  }) => {
    await analytics.track('session_ended', {
      category: 'engagement',
      duration: data.duration,
      page_view_count: data.pageViewCount,
      active_time: data.activeTime,
      idle_time: data.idleTime,
    });
  },

  // Dashboard events
  dashboardViewed: async (data: {
    widgetsDisplayed: string[];
    customizationLevel: 'default' | 'minimal' | 'custom';
    loadTime: number; // milliseconds
  }) => {
    await analytics.track('dashboard_viewed', {
      category: 'engagement',
      widgets_displayed: data.widgetsDisplayed.join(','),
      customization_level: data.customizationLevel,
      load_time: data.loadTime,
    });
  },

  widgetInteracted: async (data: {
    widgetName: string;
    interactionType: 'click' | 'hover' | 'expand' | 'customize';
    resultingAction?: string;
  }) => {
    await analytics.track('widget_interacted', {
      category: 'engagement',
      widget_name: data.widgetName,
      interaction_type: data.interactionType,
      resulting_action: data.resultingAction,
    });
  },
};